import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ActionFieldReturnType,
  AssignIssueFormValues,
} from './use-manage-issue-form';

interface ActionValidationHandlerParams {
  actionsFieldArray: {
    fields: Array<{ customId: string }>;
  };
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  actionRefs: React.MutableRefObject<Record<string, HTMLDivElement>>;
}

export const useActionValidationHandler = ({
  actionsFieldArray,
  expanded,
  setExpanded,
  actionRefs,
}: ActionValidationHandlerParams): {
  handleValidationErrors: (errors: FieldErrors<AssignIssueFormValues>) => void;
} => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['issue']);
  const expandActionsWithErrors = useCallback(
    (actionIds: string[]) => {
      actionIds.forEach((actionId) => {
        if (!expanded[actionId]) {
          setExpanded((prevExpanded) => ({
            ...prevExpanded,
            [actionId]: true,
          }));
        }
      });
    },
    [expanded, setExpanded],
  );

  const scrollToFirstError = useCallback(
    (actionId: string) => {
      setTimeout(() => {
        const actionElement = actionRefs.current[actionId];

        if (actionElement) {
          actionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    },
    [actionRefs],
  );

  const hasValidationErrors = useCallback(
    (actionError: ActionFieldReturnType['errors']): boolean => {
      if (!actionError || typeof actionError !== 'object') {
        return false;
      }

      return Object.keys(actionError).some(
        (fieldKey) => actionError[fieldKey]?.message,
      );
    },
    [],
  );

  const findActionsWithErrors = useCallback(
    (actionErrors: FieldErrors<AssignIssueFormValues>['actions']): string[] => {
      if (!Array.isArray(actionErrors)) {
        return [];
      }

      return actionErrors.reduce<string[]>((acc, actionError, index) => {
        if (hasValidationErrors(actionError)) {
          const actionField = actionsFieldArray.fields[index];
          if (actionField.customId) {
            acc.push(actionField.customId);
          }
        }
        return acc;
      }, []);
    },
    [actionsFieldArray.fields, hasValidationErrors],
  );

  const handleValidationErrors = useCallback(
    (errors: FieldErrors<AssignIssueFormValues>) => {
      if (!errors.actions) {
        return;
      }

      if (errors.actions.message) {
        enqueueSnackbar(errors.actions.message, {
          variant: 'warning',
        });
        return;
      }

      const actionsWithErrors = findActionsWithErrors(errors.actions);

      expandActionsWithErrors(actionsWithErrors);
      scrollToFirstError(actionsWithErrors[0]);
      enqueueSnackbar(t('issue:form.errorMessages.actions.missingFields'), {
        variant: 'error',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findActionsWithErrors, expandActionsWithErrors, scrollToFirstError],
  );

  return { handleValidationErrors };
};
